import { fetchFormConfig, fetchWidgetConfig } from "@/form-api"
import type { FormConfig, FormType } from "@/types/form-types"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Suspense } from "react"
import { createRoot } from "react-dom/client"
import { ErrorBoundary } from "react-error-boundary"
import { FormRender } from "./components/form-render"
import { TooltipProvider } from "./components/ui/tooltip"
import { applyThemeMode } from "./hooks/use-form-theme"
import "./index.css"
import { getHostThemeMode, watchHostThemeMode } from "./lib/utils"
import { logger } from "./utils/logger"
import { Toaster } from "./components/ui/sonner"
import { Spinner } from "./components/ui/spinner"
import { useFormStore } from "./stores/use-form-store"
import { useLeadStore } from "./stores/use-lead-store"
// import "./external.js" //temporary
import { loadExternalScriptWithCookies } from "./lib/load-logics-script.js"
import { API_URL } from "./lib/constants.js"

declare global {
	interface Window {
		__DF_MODE: string
		submissionValues: [
			{
				field_id: string
				value: string
			},
		]
		RHF: {
			unregisterField: (fieldName: string) => void
			registerField: (fieldName: string) => void
			setError: (fieldName: string, error: string) => void
			clearErrors: (FieldName: string) => void
			setHiddenFields: (fieldName: string, hidden: boolean) => void
			hiddenFields: Record<string, boolean>
			watch: any
			errors: Record<string, string>
		}
		values: Record<string, any>
		__DF_PREVIEW_CONFIG__?: FormConfig
		__DF_USER__?: {
			user_id: string
			name: string
			email: string
			type: string
			partner_id: string
			partner_type: string
		}
		setLeadParams: (params: any) => void
		setLeadMetaData: (metaData: any) => void
		__LP_PARTNERID__?: string
		__LP_COUNSELLERID__?: string
	}
}

type ScriptAttributes = {
	target_id: string
	form_id?: string
	university_id?: string
	batch_id?: string
	application_id?: string
	mode: "embedded" | "overlay"
	position: string
	type: "FORM" | "WIDGET"
	widget_id?: string
	is_preview?: boolean
	current_stage_index: number
	application_status?: string
	is_renewal?: boolean
}

const renderedForms = new Set<string>()

function getScriptAttributes(scriptEl: HTMLScriptElement): ScriptAttributes {
	return {
		target_id: scriptEl.dataset.target || "",
		form_id: scriptEl.dataset.formId || "",
		university_id: scriptEl.dataset.universityId || "",
		batch_id: scriptEl.dataset.batchId || "",
		application_id: scriptEl.dataset.applicationId || "",
		mode: (scriptEl.dataset.mode as "embedded" | "overlay") || "embedded",
		position: scriptEl.dataset.position || "center",
		type: scriptEl.dataset.type as "FORM" | "WIDGET",
		widget_id: scriptEl.dataset.widgetId || "",
		is_preview: scriptEl.dataset.isPreview === "true" ? true : false,
		current_stage_index: Number(scriptEl.dataset.currentStageIndex || "0"),
		application_status: scriptEl.dataset.applicationStatus || "",
		is_renewal: scriptEl.dataset.isRenewal === "true" ? true : false,
	}
}

function resolveScriptTag(): HTMLScriptElement | null {
	return document.getElementById("__DF_SCRIPT__") as HTMLScriptElement | null
}

function createOverlayWrapper(position: string): {
	container: HTMLElement
	backdrop: HTMLElement
} {
	const backdrop = document.createElement("div")
	backdrop.className = "df-overlay-backdrop"

	const container = document.createElement("div")
	container.className = `df-overlay-container df-${position}`

	document.body.appendChild(backdrop)
	document.body.appendChild(container)

	return { container, backdrop }
}

function waitForElement(id: string, timeout = 5000): Promise<HTMLElement> {
	return new Promise((resolve, reject) => {
		const intervalTime = 100
		const maxAttempts = timeout / intervalTime
		let attempts = 0

		const interval = setInterval(() => {
			const el = document.getElementById(id)
			if (el) {
				clearInterval(interval)
				resolve(el)
			}
			if (++attempts >= maxAttempts) {
				clearInterval(interval)
				reject(new Error(`Element #${id} not found after ${timeout}ms`))
			}
		}, intervalTime)
	})
}

async function safeBootstrap({
	formConfig,
	target_id,
	form_id,
	university_id = "",
	batch_id = "",
	mode,
	position,
	force = false,
	application_id,
	type = "FORM",
	widget_id,
	is_preview,
	current_stage_index,
	application_status,
	is_lead_punch = false,
	is_referral_widget = false,
	source = "ORGANIC",
	is_renewal = false,
}: {
	formConfig: FormConfig
	target_id: string
	form_id?: string
	university_id: string
	batch_id: string
	mode: string
	position: string
	force: boolean
	application_id?: string
	type: FormType
	widget_id?: string
	is_preview: boolean
	current_stage_index: number
	application_status: string
	is_lead_punch: boolean
	is_referral_widget: boolean
	source: string
	is_renewal: boolean
}) {
	const key =
		type === "FORM"
			? `${form_id}@${target_id}@${location.pathname}`
			: `${widget_id}@${target_id}@${location.pathname}`
	if (!force && renderedForms.has(key)) {
		logger.info(`[FormRenderer] Form with key "${key}" already rendered.`)
		return
	}
	if (force) renderedForms.delete(key)
	if (type === "WIDGET") {
		formConfig.widget_id = widget_id
	}

	try {
		let container: HTMLElement

		if (mode === "overlay") {
			const { container: c, backdrop } = createOverlayWrapper(position)
			container = c

			if (formConfig?.show_close_icon) {
				const closeBtn = document.createElement("div")
				closeBtn.className = "df-close-icon"
				closeBtn.innerHTML = "&times;"
				closeBtn.onclick = () => {
					container.remove()
					backdrop.remove()
					renderedForms.delete(key)
				}
				container.appendChild(closeBtn)
			}
		} else {
			container = await waitForElement(target_id)
			container.innerHTML = ""
			container.style.height = "100%"
			container.style.overflowY = "auto"
		}

		const font = document.createElement("style")
		font.innerHTML = `
            @import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");
        `
		document.head.appendChild(font)

		const root = createRoot(container)
		const queryClient = new QueryClient()
		const formId = useFormStore.getState().config?.form_id
		const widgetId = useFormStore.getState().config?.widget_id
		const applicationId = useFormStore.getState().applicationId
		const currentStageIdx = useFormStore.getState().currentStageIndex

		if (
			applicationId !== application_id ||
			formId !== formConfig.form_id ||
			(formConfig.type === "WIDGET" && widgetId !== formConfig.widget_id)
		) {
			useFormStore.getState().reset()
		}
		const maxStages = formConfig.stages.length
		if (currentStageIdx >= maxStages) {
			useFormStore.getState().setCurrentStageIndex(0)
		}

		if (formConfig.condition_logic_url) {
			const BASE_URL = API_URL.endsWith("/") ? API_URL : `${API_URL}/`
			await loadExternalScriptWithCookies(
				`${BASE_URL}media?path=${formConfig.condition_logic_url}`,
			)
		}

		root.render(
			<QueryClientProvider client={queryClient}>
				<TooltipProvider>
					{/* <FormEngine
					config={formConfig}
					university_id={university_id}
					type={type}
					application_id={application_id}
					isPreview={is_preview}
				/> */}
					<ErrorBoundary
						fallback={<div>Something went wrong</div>}
						resetKeys={[
							window.location.pathname,
							window.location.search,
						]}
					>
						<Suspense
							fallback={
								<div className="flex h-full items-center justify-center">
									<Spinner />
								</div>
							}
						>
							<Toaster />
							{formConfig.stages.length >= 1 && (
								<FormRender
									config={formConfig}
									universityId={university_id}
									batchId={batch_id}
									type={type}
									applicationId={application_id!}
									isPreviewMode={is_preview}
									currentStageIdx={current_stage_index}
									applicationStatus={application_status}
									isLeadPunch={is_lead_punch}
									isReferralWidget={is_referral_widget}
									source={source}
									isRenewal={is_renewal}
								/>
							)}
						</Suspense>
					</ErrorBoundary>
				</TooltipProvider>
			</QueryClientProvider>,
		)
		renderedForms.add(key)
	} catch (e) {
		logger.error("[FormRenderer] Failed to render form:", e)
	}
}

async function bootstrap(formConfig?: FormConfig, force = false) {
	const scriptEl = resolveScriptTag()
	if (!scriptEl) {
		logger.error("[FormRenderer] Script tag not found.")
		return
	}

	const {
		target_id,
		form_id,
		university_id,
		batch_id,
		mode,
		position,
		application_id,
		type,
		widget_id,
		is_preview,
		current_stage_index,
		application_status,
		is_renewal,
	} = getScriptAttributes(scriptEl)

	if (!target_id) {
		logger.error('[FormRenderer] Missing "target_id" on <script>')
		return
	}

	// Detect initial mode
	let themeMode = getHostThemeMode()
	applyThemeMode(themeMode)

	// Watch for host theme changes
	watchHostThemeMode(newMode => {
		themeMode = newMode
		applyThemeMode(themeMode)
	})

	if (formConfig) {
		const { form_id = "", batch_id = "" } = formConfig
		await safeBootstrap({
			formConfig,
			target_id,
			form_id,
			university_id: formConfig.university_id || university_id || "",
			batch_id,
			mode,
			position,
			force,
			type: formConfig.type,
			application_id,
			widget_id,
			is_preview: is_preview || false,
			current_stage_index: current_stage_index,
			application_status: application_status || "",
			is_lead_punch: formConfig.is_lead_punch || false,
			is_referral_widget: formConfig.is_referral_widget || false,
			source: formConfig.source || "ORGANIC",
			is_renewal: is_renewal || false,
		})
	} else {
		if (!university_id) {
			logger.error('[FormRenderer] Missing "university_id" on <script>')
			return
		}
		if (type === "WIDGET" && !widget_id) {
			logger.error('[FormRenderer] Missing "widget_id" for widget type')
			return
		}

		if (type == "FORM" && !form_id && !batch_id) {
			logger.error(
				'[FormRenderer] Missing "form_id" or "university_id" or "batch_id" on <script>',
			)
			return
		}

		const config: FormConfig | null | undefined =
			type == "FORM" && form_id && batch_id
				? await fetchFormConfig(form_id, university_id, batch_id)
				: widget_id
					? await fetchWidgetConfig(widget_id, university_id)
					: null

		if (!config) {
			logger.error(
				`[FormRenderer] Failed to fetch ${type} config for ID "${type === "FORM" ? form_id : widget_id}" and university "${university_id}"`,
			)
			return
		}

		await safeBootstrap({
			formConfig: config,
			target_id,
			form_id,
			university_id,
			batch_id: batch_id ?? "",
			mode,
			position,
			force,
			type,
			application_id,
			widget_id,
			is_preview: is_preview || false,
			current_stage_index: current_stage_index,
			application_status: application_status || "",
			is_lead_punch: config.is_lead_punch || false,
			is_referral_widget: config.is_referral_widget || false,
			source: config.source || "ORGANIC",
			is_renewal: is_renewal || false,
		})
	}
}

// 💡 Initialize
document.addEventListener("DOMContentLoaded", async () => {
	logger.info(
		"[FormRenderer] Initializing form renderer from DOMContentLoaded...",
	)
	bootstrap(structuredClone(window.__DF_PREVIEW_CONFIG__))
})

// 🔄 Re-render on config update
window.addEventListener("df-config-updated", async () => {
	logger.info("[FormRenderer] Config updated via event, re-rendering...")
	bootstrap(structuredClone(window.__DF_PREVIEW_CONFIG__), true)
})

window.setLeadParams = function (values: any) {
	useLeadStore.setState({ params: values })
}

window.setLeadMetaData = function (values: any) {
	useLeadStore.setState({ metaData: values })
}

logger.info("[FormRenderer] Initializing form renderer from IIFE....")
bootstrap(structuredClone(window.__DF_PREVIEW_CONFIG__), true)

// 👁️ Watch for DOM changes
// const observer = new MutationObserver(() => {
// 	logger.info("[FormRenderer] DOM mutation detected, re-rendering...")
// 	bootstrap(structuredClone(window.__DF_PREVIEW_CONFIG__))
// })
// observer.observe(document.body, { childList: true, subtree: true })
